import Cookies from 'js-cookie'
import client from '~/lib/client/client'
import { randomUUID } from '~/lib/crypto'
import type { SignFn } from './signatures'

export interface Analytics {
  init: () => void
  set: (data: Record<string, any>) => Record<string, any>
  page: (data: Record<string, any>) => void
  track: (event: string, data: Record<string, any>) => void
  destroy: () => void
}

const analytics = ((): Analytics => {
  let loaded = false
  let session: string | null = null
  let tracking: Record<string, any> = {}

  let _sign: SignFn = async (event: string, data: any) => ({
    p: [event, JSON.stringify(data), Date.now()],
    s: null,
  })

  const send = async (eventname: string, data: any) => {
    const payload = await _sign(eventname, data)
    if (!payload) {
      return null
    }
    const [resp, err] = await client({
      method: 'POST',
      path: '/api/client/events',
      body: payload,
      headers: {},
    })
    if (err) {
      console.error(err)
      return null
    }
    return resp
  }

  const dispatch = (event: string, data: Record<string, any> = {}) => {
    const device_id = Cookies.get('device_id')
    const user_id = Cookies.get('auth.user_id')

    const d = {
      path: window.location.pathname,
      user_id,
      device_id,
      session,
      ...data,
      ...tracking,
    }
    send(event, d)
  }

  let queue: Array<{ event: string; data: Record<string, any> }> = []

  const flush = () => {
    queue.forEach(({ event, data }) => dispatch(event, data))
    queue = []
  }

  const enqueue = (event: string, data: Record<string, any>) => {
    queue.push({ event, data })
    if (loaded) flush()
  }

  const page = (data: Record<string, any> = {}) => {
    return enqueue('pageview', {
      ...data,
    })
  }

  const track = (event: string, data: Record<string, any> = {}) => {
    return enqueue(event, {
      ...data,
    })
  }

  const set = (data: Record<string, any>) => {
    tracking = { ...tracking, ...data }
    tracking = JSON.parse(JSON.stringify(tracking))
    return tracking
  }

  const destroy = () => {
    loaded = false
    queue = []
    tracking = {}
  }

  const init = async () => {
    if (loaded) return

    session = sessionStorage.getItem('session_id')

    if (!session) {
      session = randomUUID()
      sessionStorage.setItem('session_id', session)
    }

    const { sign } = await import('./signatures')
    _sign = sign as SignFn

    track('loaded', {
      url: window.location.href,
    })

    loaded = true
    flush()
  }

  return { init, page, track, set, destroy }
})()

export default analytics
